import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useMentorSession } from '../context/TrackerContext';
import { getMentorStudentProfiles, updateStudentPhase } from '../lib/api';
import type { CoursePhase, StudentProfile } from '../lib/api';

const PHASES: CoursePhase[] = ['First 5 Days', 'Mid Course', 'Last 10 Days'];

const PHASE_CONFIG: Record<CoursePhase, {
  label: string; bg: string; headerBg: string; color: string; border: string; dot: string;
}> = {
  'First 5 Days': { label: 'First 5 Days',  bg: '#fffbeb', headerBg: '#fef3c7', color: '#92400e', border: '#fde68a', dot: '#f59e0b' },
  'Mid Course':   { label: 'Mid Course',     bg: '#faf5ff', headerBg: '#ede9fe', color: '#4c1d95', border: '#ddd6fe', dot: '#7c3aed' },
  'Last 10 Days': { label: 'Last 10 Days',   bg: '#f0fdf4', headerBg: '#dcfce7', color: '#14532d', border: '#bbf7d0', dot: '#16a34a' },
};

const PhaseCard: React.FC<{
  profile: StudentProfile;
  currentPhase: CoursePhase;
  movingId: string | null;
  onMove: (studentId: string, phase: CoursePhase) => void;
}> = ({ profile, currentPhase, movingId, onMove }) => {
  const cfg = PHASE_CONFIG[currentPhase];
  const isMoving = movingId === profile.studentId;

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 mb-3 transition-all hover:shadow-md"
      style={{ borderColor: cfg.border, opacity: isMoving ? 0.6 : 1 }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: '#2b2240', color: '#F0B429' }}>
          {profile.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-gray-900 truncate">{profile.name}</p>
          <p className="text-xs text-gray-500 truncate">{profile.course}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-gray-400 font-mono">{profile.studentId}</span>
            {profile.sessionCategory && (
              <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: '#e0f2fe', color: '#075985' }}>
                {profile.sessionCategory}
              </span>
            )}
          </div>
        </div>
        {isMoving && (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin flex-shrink-0 mt-1"/>
        )}
      </div>

      {/* Move to other phase */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400 mb-2">Move to:</p>
        <div className="flex gap-1.5 flex-wrap">
          {PHASES.filter(p => p !== currentPhase).map(p => {
            const pc = PHASE_CONFIG[p];
            return (
              <button key={p}
                disabled={movingId !== null}
                onClick={() => onMove(profile.studentId, p)}
                className="text-xs px-2.5 py-1.5 rounded-lg font-semibold border transition-all disabled:opacity-40 hover:opacity-80 active:scale-95"
                style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}>
                {p === 'First 5 Days' ? 'Day 1–5' : p === 'Mid Course' ? 'Mid Course' : 'Last 10 Days'}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MentorPhaseTracker: React.FC = () => {
  const { mentorId, mentorName } = useMentorSession();
  const [profiles, setProfiles] = useState<StudentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMentorStudentProfiles(mentorId);
      setProfiles(res.profiles);
    } catch (err: any) {
      toast.error('Failed to load students: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [mentorId]);

  useEffect(() => { load(); }, [load]);

  const handleMove = async (studentId: string, phase: CoursePhase) => {
    setMovingId(studentId);
    try {
      await updateStudentPhase(mentorId, studentId, phase);
      setProfiles(prev => prev.map(p => p.studentId === studentId ? { ...p, phase } : p));
      toast.success(`Moved to "${phase}"`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setMovingId(null);
    }
  };

  const filtered = profiles.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.course.toLowerCase().includes(search.toLowerCase()) ||
    p.studentId.toLowerCase().includes(search.toLowerCase())
  );

  const byPhase = (phase: CoursePhase) =>
    filtered.filter(p => (p.phase || 'First 5 Days') === phase);

  return (
    <TrackerLayout>
      <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 65px)' }}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#2b2240' }}>Phase Tracker</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {mentorName} &nbsp;·&nbsp; {profiles.length} students
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input type="text" placeholder="Search students…"
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#2b2240] w-52"/>
            </div>
            <button onClick={load}
              className="px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading your students…</div>
        ) : profiles.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
            <p className="text-amber-800 font-semibold">No students assigned to you yet.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-3 gap-4">
            {PHASES.map(phase => {
              const cfg = PHASE_CONFIG[phase];
              const cols = byPhase(phase);
              return (
                <div key={phase} className="flex flex-col rounded-xl border overflow-hidden" style={{ borderColor: cfg.border }}>
                  {/* Column header */}
                  <div className="px-4 py-3 flex-shrink-0 flex items-center justify-between" style={{ background: cfg.headerBg }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cfg.dot }}/>
                      <span className="font-bold text-sm" style={{ color: cfg.color }}>{cfg.label}</span>
                    </div>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.6)', color: cfg.color }}>
                      {cols.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div className="flex-1 overflow-y-auto p-3" style={{ background: cfg.bg }}>
                    {cols.length === 0 ? (
                      <div className="text-center py-10 text-xs" style={{ color: cfg.color, opacity: 0.5 }}>
                        No students in this phase
                      </div>
                    ) : (
                      cols.map(p => (
                        <PhaseCard
                          key={p.studentId}
                          profile={p}
                          currentPhase={phase}
                          movingId={movingId}
                          onMove={handleMove}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default MentorPhaseTracker;
